import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../../hooks/useFetch"; // same depth as Gumball.jsx — adjust if this file lands elsewhere

const GAME_LABELS = {
  all: "All Games",
  tinythink: "TinyThink",
  iqtester: "IQTester",
  emojigrid: "Emoji Hunt",
  dronedash: "DroneDash",
};

const GAME_ICONS = {
  tinythink: "🧠",
  iqtester: "🎯",
  emojigrid: "🎈",
  dronedash: "🚁",
};

/**
 * Drop in anywhere a parent/mentor views a child's account — e.g. as its
 * own screen/tab, or inside a settings/profile area. Pulls the logged-in
 * user from auth state itself, same as Gumball.jsx — no props required
 * beyond an optional onClose.
 */
export default function GumballHistoryPage({ onClose }) {
  const authState = useSelector((state) => state.authReducer);
  const [fetchData] = useFetch();
  const userId = authState?.user?._id;
  const userName = authState?.user?.name;
  const token = authState?.token;

  const [activeGame, setActiveGame] = useState("all");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadHistory = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const url = activeGame === "all" ? "/gumball/history/all" : `/gumball/${activeGame}/history`;
      const res = await fetchData(
        { url, method: "post", data: { userId }, headers: { Authorization: token } },
        { silent: true }
      );
      if (res?.success) {
        setHistory(res.history || []);
      } else {
        setError("Couldn't load reward history.");
      }
    } catch (e) {
      console.error("GumballHistoryPage load error:", e);
      setError("Couldn't load reward history.");
    } finally {
      setLoading(false);
    }
  }, [activeGame, userId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  return (
    <div className="w-full rounded-2xl bg-[#181024] border border-violet-500/30 p-4 text-white mt-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-cyan-200 text-lg font-bold">Gumball Reward History</h2>
          {userName && <p className="text-xs text-white">{userName}</p>}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="w-fit px-3 py-1 text-xs rounded-lg border border-white/20 hover:border-white/40"
          >
            Close
          </button>
        )}
      </div>

      {/* Game filter tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {["all", "tinythink", "iqtester", "emojigrid", "dronedash"].map((key) => (
          <button
            key={key}
            onClick={() => setActiveGame(key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border transition-colors ${
              activeGame === key
                ? "bg-violet-600 border-violet-400 text-white"
                : "bg-transparent border-white/15 text-white/60 hover:border-white/30"
            }`}
          >
            {key !== "all" && GAME_ICONS[key] + " "}
            {GAME_LABELS[key]}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-white/50 text-sm py-6">Loading…</div>}
      {error && <div className="text-center text-red-300 text-sm py-6">{error}</div>}

      {!loading && !error && (
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {history.length === 0 && (
            <div className="text-center text-white/40 text-sm py-8">No rewards given yet.</div>
          )}
          {history.map((entry) => (
            <div
              key={entry._id}
              className="rounded-xl bg-black/25 border border-white/10 p-3 flex flex-col gap-1"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">
                  {activeGame === "all" && GAME_ICONS[entry.gameKey] ? GAME_ICONS[entry.gameKey] + " " : ""}
                  {entry.rewardGiven}
                </span>
                <span className="text-[11px] text-white/50">
                  {entry.redeemedOn ? new Date(entry.redeemedOn).toLocaleDateString() : ""}
                </span>
              </div>
              {activeGame === "all" && (
                <span className="text-[11px] text-violet-300">{GAME_LABELS[entry.gameKey] || entry.gameKey}</span>
              )}
              <div className="flex gap-3 text-[11px] text-white/60">
                <span>🔥 {entry.achievedStreak} day streak</span>
                <span>⭐ {entry.achievedPoints} pts</span>
              </div>
              {entry.remarks && <div className="text-xs text-white/70 italic">"{entry.remarks}"</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
